import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { AgentChannelName } from "./AgentChannel.ts";

/**
 * Deployment types for agent installations.
 * @since 1.0.0
 */
export const DeploymentType = Schema.Literal("local-mac", "cloud-vps");
export type DeploymentType = Schema.Schema.Type<typeof DeploymentType>;

/**
 * Cloud provider for cloud-vps installs. Plain varchar in PG (NOT a PG enum —
 * avoids the enum-add-isolation two-deploy rule, see packages/db/CLAUDE.md).
 * @since 1.0.0
 */
export const CloudProvider = Schema.Literal("hetzner", "digitalocean", "ovh");
export type CloudProvider = Schema.Schema.Type<typeof CloudProvider>;

/**
 * Which browser the provisioned OpenClaw box runs. Plain varchar in PG (NOT a PG
 * enum — avoids the enum-add-isolation two-deploy rule, see packages/db/CLAUDE.md).
 * Defaults to `chrome` (no behavior change); `camoufox` is the anti-detect option.
 * Single source of truth — consumed by CLA-656/657/659; never re-declare downstream.
 * @since 1.0.0
 */
export const BrowserKind = Schema.Literal("chrome", "camoufox");
export type BrowserKind = Schema.Schema.Type<typeof BrowserKind>;

/**
 * The supported cloud-vps regions — Hetzner Cloud location slugs. The single
 * source of truth shared across the DB model, the api validation seam, and the
 * web intake worksheet's Region select. Stored as a plain varchar in PG (NOT a
 * PG enum — validation lives in the api layer, see packages/db/CLAUDE.md), so a
 * new location is a one-line addition here with no migration.
 *
 * @since 1.0.0
 */
export const HETZNER_LOCATIONS = ["ash", "hil", "fsn1", "nbg1", "hel1", "sin"] as const;

/**
 * A supported cloud-vps region (Hetzner Cloud location slug).
 * @since 1.0.0
 */
export const HetznerLocation = Schema.Literal(...HETZNER_LOCATIONS);
export type HetznerLocation = Schema.Schema.Type<typeof HetznerLocation>;

/**
 * Narrowing predicate: whether an arbitrary string is a supported
 * {@link HetznerLocation}. Backs the api-side validation on intake write and
 * agent create.
 *
 * @since 1.0.0
 */
export const isValidHetznerLocation = (value: string): value is HetznerLocation =>
  (HETZNER_LOCATIONS as ReadonlyArray<string>).includes(value);

/**
 * How this agent's platform seat is billed, persisted at creation time from the
 * billing decision that produced the row (CLA-647):
 *
 *  - `charged` — the prorated seat was charged on the customer's platform Stripe
 *    subscription (admin `billing: "charge"`, or the self-serve procure settle
 *    path, or the intake-seeded first agent already covered by onboarding).
 *  - `comped` — the seat was comped (admin `billing: "skip"`), NO Stripe mutation.
 *  - `invoice` — the seat rides a hosted approval invoice (admin `billing: "invoice"`).
 *  - `reseller` — a reseller-attributed customer billed on the connected-account
 *    subscription; platform seat billing never applies.
 *
 * Plain varchar in PG (NOT a PG enum — avoids the enum-add-isolation two-deploy
 * rule, see packages/db/CLAUDE.md). NULLABLE with no default: `null` marks a
 * legacy row created before this field existed and behaves exactly as a
 * `charged` seat on deprovision (the pre-feature behavior).
 *
 * @since 1.0.0
 */
export const SeatBillingMode = Schema.Literal("charged", "comped", "invoice", "reseller");
export type SeatBillingMode = Schema.Schema.Type<typeof SeatBillingMode>;

/**
 * Installation workflow phases tracked through provisioning to completion.
 * @since 1.0.0
 */
export const InstallationPhase = Schema.Literal(
  "intake",
  "connecting",
  "provisioning",
  "testing",
  "invoicing",
  "complete",
);
export type InstallationPhase = Schema.Schema.Type<typeof InstallationPhase>;

/**
 * Provisioning status for the installation step.
 * @since 1.0.0
 */
export const ProvisioningStatus = Schema.Literal("pending", "in-progress", "success", "failed");
export type ProvisioningStatus = Schema.Schema.Type<typeof ProvisioningStatus>;

/**
 * Agent installation status enum for lifecycle tracking.
 * @since 1.0.0
 */
export const AgentInstallationStatus = Schema.Literal(
  "provisioning",
  "pending_install",
  "active",
  "stopped",
  "error",
  "deleted",
  "deprovisioned",
);
export type AgentInstallationStatus = Schema.Schema.Type<typeof AgentInstallationStatus>;

/**
 * Install-fee lifecycle status enum.
 * @since 1.0.0
 */
export const InstallFeeStatus = Schema.Literal(
  "pending",
  "priced",
  "comped",
  "invoiced",
  "paid",
  "overdue",
  "void",
);
export type InstallFeeStatus = Schema.Schema.Type<typeof InstallFeeStatus>;

/**
 * Persisted form of the OpenClaw health snapshot reported on each
 * heartbeat (C3b). Identical to the wire shape sent by the agent's
 * heartbeat loop — stored verbatim in `oc_health_raw` so the admin UI
 * and alert pipeline can surface degradation reasons without parsing
 * a stringified blob.
 *
 * @since 1.0.0
 */
export const OcHealthSnapshot = Schema.Struct({
  status: Schema.Literal("ok", "degraded", "down"),
  reasonCode: Schema.NullOr(Schema.String),
  observedAt: Schema.String,
  /**
   * CLA-772: the full `openclaw health --json` document carried verbatim (or a
   * `{ truncated, bytes }` marker above the size cap). `Schema.Unknown` because
   * the contents are opaque to this repo and never parsed. Optional: pre-CLA-772
   * rows and failed/cold-boot probes carry no `raw`. Without this field the
   * encode step silently strips `raw` from every admin response.
   */
  raw: Schema.optional(Schema.Unknown),
});
export type OcHealthSnapshot = Schema.Schema.Type<typeof OcHealthSnapshot>;

/**
 * Compact projection of clawd's on-box DoctorLoop assessment (CLA-775),
 * persisted verbatim in `oc_assessment` on each heartbeat that carries it.
 *
 * `status` is the DoctorLoop's mapped heartbeat status: this is where the
 * wire-schema's long-admitted `"degraded"` value FINALLY becomes reachable —
 * `ocHealthRaw`/`ocHealthOk` stay ok/down-only by design so the degraded
 * signal never fires the ok↔down alert pipeline or blocks provisioning
 * reconciliation. `topFindings` is the severity-ranked head of the cycle's
 * findings (checkId + severity only); `findingCount` is the full count.
 *
 * @since 1.0.0
 */
export const OcAssessmentSummary = Schema.Struct({
  status: Schema.Literal("ok", "degraded", "down"),
  findingCount: Schema.Int,
  topFindings: Schema.Array(Schema.Struct({
    checkId: Schema.String,
    severity: Schema.String,
  })),
  observedAt: Schema.String,
});
export type OcAssessmentSummary = Schema.Schema.Type<typeof OcAssessmentSummary>;

/**
 * AgentInstallation model representing a provisioned OpenClaw instance.
 *
 * Maps 1:1 to the `agent_installations` table and the `AgentInstallationRecord` interface
 * in `packages/auto/src/hetzner/types.ts`.
 *
 * @since 1.0.0
 */
export class AgentInstallation extends Model.Class<AgentInstallation>("AgentInstallation")({
  id: Model.Generated(Schema.String),
  name: Schema.NonEmptyTrimmedString,
  customerId: Schema.NullOr(Schema.String),
  /**
   * Set ONLY on the intake-seeded first agent (FK → intakes.id). Present =>
   * the onboarding wizard filters the catalog to the intake's selected
   * channels/connectors; null => self-serve agent with the full catalog.
   */
  intakeId: Schema.NullOr(Schema.String),
  frpToken: Schema.NullOr(Schema.String),
  serverId: Schema.String,
  /**
   * OVH async-order handle: the `orderId` returned by checkout, persisted
   * immediately so a retried provisioning activity resumes the pending order
   * instead of buying a second VPS. Null for Hetzner/DO rows and until the
   * OVH order is placed.
   */
  providerOrderId: Schema.NullOr(Schema.String),
  publicIp: Schema.String,
  tailscaleIp: Schema.String,
  tailscaleHostname: Schema.String,
  serverType: Schema.String,
  location: Schema.String,
  gatewayToken: Schema.String,
  /**
   * SHA-256 hex digest of the plaintext gateway token (CLA-845), backing the
   * indexed `findByGatewayToken` lookup. `null` on legacy rows until the
   * accessor lazily backfills it on the first hash-miss authentication.
   */
  gatewayTokenHash: Schema.NullOr(Schema.String),
  vncPassword: Schema.String,
  /**
   * Per-install WebDAV credential (CLA-884), encrypted at rest. `null` until
   * lazily minted (fm-api) on the first workspace mount.
   */
  webdavPassword: Schema.NullOr(Schema.String),
  /**
   * SHA-256 hex digest of the plaintext WebDAV password (CLA-884), backing the
   * indexed `findByWebdavPasswordHash` lookup used by the k8s-proxy gate. `null`
   * until the password is minted (no lazy backfill — minted-with-hash from day
   * one).
   */
  webdavPasswordHash: Schema.NullOr(Schema.String),
  /**
   * When the cloud-vps provisioning workflow was dispatched for this install
   * (CLA-484). `null` => provisioning has never been dispatched, so the region
   * is still customer-changeable on the Install step; once set the region is
   * immutable. Stamped at the single dispatch point (onboarding `getInstall`).
   */
  provisioningDispatchedAt: Schema.NullOr(Schema.DateTimeUtc),
  status: Schema.optionalWith(AgentInstallationStatus, { default: () => "provisioning" as const }),
  installFeeStatus: Schema.optionalWith(InstallFeeStatus, { default: () => "pending" as const }),
  installFeeAmount: Schema.NullOr(Schema.Int),
  installFeeCompReason: Schema.NullOr(Schema.String),
  installInvoiceId: Schema.NullOr(Schema.String),
  installInvoiceUrl: Schema.NullOr(Schema.String),
  installInvoicedAt: Schema.NullOr(Schema.DateTimeUtc),
  installPaidAt: Schema.NullOr(Schema.DateTimeUtc),
  managedOffered: Schema.optionalWith(Schema.Boolean, { default: () => false }),
  managedOfferAmount: Schema.NullOr(Schema.Int),
  managedOfferTerms: Schema.NullOr(Schema.String),
  managedSignupUrl: Schema.NullOr(Schema.String),
  managedSignupSentAt: Schema.NullOr(Schema.DateTimeUtc),
  deprovisionedAt: Schema.NullOr(Schema.DateTimeUtc),
  deprovisionReason: Schema.NullOr(Schema.String),
  /**
   * Scheduled-cancellation request (CLA-1003). A customer's cancel from their
   * dashboard records a PENDING request — it destroys nothing; the period-end
   * sweep tears the agent down later. All `null` on a row with no pending
   * request (every pre-feature row). `cancellationRequestedBy` is the requesting
   * person id; `cancellationEffectiveAt` is the subscription's current-period-end
   * (when the teardown becomes due, and the date the dashboard renders).
   */
  cancellationRequestedAt: Schema.NullOr(Schema.DateTimeUtc),
  cancellationRequestedBy: Schema.NullOr(Schema.String),
  cancellationEffectiveAt: Schema.NullOr(Schema.DateTimeUtc),
  deploymentType: Schema.NullOr(DeploymentType),
  /**
   * How the platform seat is billed for this agent (CLA-647). `null` = legacy
   * row (created before the field existed); it behaves as `charged` on
   * deprovision so a legacy fleet decrements seats exactly as before.
   */
  seatBilling: Schema.NullOr(SeatBillingMode),
  /**
   * When this row was created by the admin `installs.adopt` RPC to bring a
   * hand-managed box (MAXClaw / Venture) under clawd monitoring (CLA-778).
   * `null` => a normally provisioned install. The explicit adoption marker —
   * `deploymentType` stays the box's REAL OS (`local-mac` / `cloud-vps`) so no
   * non-exhaustive `deploymentType` consumer is perturbed.
   */
  adoptedAt: Schema.NullOr(Schema.DateTimeUtc),
  cloudProvider: Schema.optionalWith(CloudProvider, { default: () => "digitalocean" as const }),
  browser: Schema.optionalWith(BrowserKind, { default: () => "chrome" as const }),
  doDnsRecordId: Schema.NullOr(Schema.String),
  installToken: Schema.NullOr(Schema.String),
  installTokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  agentSecret: Schema.NullOr(Schema.String),
  agePublicKey: Schema.NullOr(Schema.String),
  agentVersionPin: Schema.NullOr(Schema.String),
  updateChannelOverride: Schema.NullOr(AgentChannelName),
  agentVersion: Schema.NullOr(Schema.String),
  /**
   * OpenClaw version reported by clawd's local `openclaw health --json` probe
   * (CLA-320). `null` => never reported. Surfaced in admin next to
   * `agentVersion` so operators can see the box's OpenClaw version drift.
   */
  ocVersion: Schema.NullOr(Schema.String),
  lastUpdateCheckAt: Schema.NullOr(Schema.DateTimeUtc),
  lastUpdateOutcome: Schema.NullOr(Schema.String),
  updateTargetVersion: Schema.NullOr(Schema.String),
  ocHealthOk: Schema.NullOr(Schema.Boolean),
  ocHealthObservedAt: Schema.NullOr(Schema.DateTimeUtc),
  ocHealthRaw: Schema.NullOr(OcHealthSnapshot),
  /**
   * CLA-775: the latest compact DoctorLoop assessment the agent has reported
   * via heartbeat. `null` until the box reports its first assessment cycle.
   * Its `status` is the only place `"degraded"` is ever surfaced — see
   * {@link OcAssessmentSummary}.
   */
  ocAssessment: Schema.NullOr(OcAssessmentSummary),
  /**
   * CLA-782: the latest redacted OpenClaw config snapshot reported by clawd
   * on its own RPC (sibling issue CLA-807). `null` => never reported. The
   * blob is customer-shaped and opaque — every secret-bearing value is
   * fingerprinted on the box before it crosses the wire — so `Schema.Unknown`
   * (no invented wrapper struct; contrast CLA-775's named-struct rule, which
   * applies when this repo defines the shape). Latest-only, no history.
   */
  ocConfigRedacted: Schema.NullOr(Schema.Unknown),
  /** Content hash of the snapshot the box reported. `null` => never reported. */
  ocConfigHash: Schema.NullOr(Schema.String),
  /** When the box captured the snapshot. `null` => never reported. */
  ocConfigCapturedAt: Schema.NullOr(Schema.DateTimeUtc),
  /**
   * CLA-784: remote-support consent as last reported by the box's heartbeat.
   * `false` (the default) until the box explicitly opts in — the unit env
   * file on the box is the consent store and the heartbeat is the ONLY
   * reporter; no server-side path ever sets this `true`.
   */
  remoteSupportEnabled: Schema.Boolean,
  /** When `remoteSupportEnabled` last transitioned. `null` => never reported. */
  remoteSupportChangedAt: Schema.NullOr(Schema.DateTimeUtc),
  /** Disclosure version tag acknowledged at the last transition. */
  remoteSupportDisclosure: Schema.NullOr(Schema.String),
  installerConfigIssuedAt: Schema.NullOr(Schema.DateTimeUtc),
  paidAt: Schema.NullOr(Schema.DateTimeUtc),
  completedAt: Schema.NullOr(Schema.DateTimeUtc),
  deletedAt: Schema.NullOr(Schema.DateTimeUtc),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
